import { useEffect, useRef, useState } from 'react';

/**
 * In-page confirm and prompt dialogs, instead of the browser's own.
 *
 * Replaced window.confirm / window.prompt everywhere (asked for 2026-10-06): the
 * browser's boxes say "<this site> says", look like a warning from the browser
 * rather than part of the product, cannot be styled, and some browsers offer to
 * suppress them - after which every "are you sure?" silently answers no.
 *
 * Same shape as the calls they replace, so a call site changes by one word:
 *
 *   if (!(await confirmDialog('Publish this coupon?\n\nIt is visible at once.'))) return;
 *   const reason = await promptDialog('Why? Recorded in the audit trail.');
 *
 * A message with a blank line splits there: the first part is the title, the
 * rest the explanation. Mounted once, at the root, as <DialogHost />.
 */

interface DialogOptions {
  /** The confirm button. Defaults: "Yes, continue" for a question, "Continue" for a prompt. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** A red confirm button, for things that cannot be undone. */
  danger?: boolean;
  /** A text box rather than a one-line field, for longer notes. */
  multiline?: boolean;
  /** Shown under the field. */
  hint?: string;
}

interface Pending extends DialogOptions {
  kind: 'confirm' | 'prompt';
  message: string;
  defaultValue: string;
  resolve: (value: boolean | string | null) => void;
}

let enqueue: ((dialog: Pending) => void) | null = null;

export function confirmDialog(message: string, options: DialogOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    if (enqueue === null) {
      // No host mounted - never true by accident.
      resolve(false);
      return;
    }
    enqueue({ ...options, kind: 'confirm', message, defaultValue: '', resolve: (v) => resolve(v === true) });
  });
}

export function promptDialog(message: string, defaultValue = '', options: DialogOptions = {}): Promise<string | null> {
  return new Promise((resolve) => {
    if (enqueue === null) {
      resolve(null);
      return;
    }
    enqueue({ ...options, kind: 'prompt', message, defaultValue, resolve: (v) => resolve(typeof v === 'string' ? v : null) });
  });
}

export function DialogHost() {
  const [queue, setQueue] = useState<Pending[]>([]);
  const [value, setValue] = useState('');
  const fieldRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  const current = queue[0] ?? null;

  useEffect(() => {
    enqueue = (dialog) => setQueue((q) => [...q, dialog]);
    return () => { enqueue = null; };
  }, []);

  // On each new dialog: remember where focus was, prefill, and focus the right control.
  useEffect(() => {
    if (current === null) return;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setValue(current.defaultValue);
    window.setTimeout(() => {
      if (current.kind === 'prompt') {
        fieldRef.current?.focus();
        fieldRef.current?.select();
      } else {
        confirmRef.current?.focus();
      }
    }, 0);
  }, [current]);

  if (current === null) return null;

  function close(result: boolean | string | null) {
    if (current === null) return;
    current.resolve(result);
    setQueue((q) => q.slice(1));
    window.setTimeout(() => returnFocus.current?.focus(), 0);
  }

  const cancel = () => close(current.kind === 'prompt' ? null : false);
  const accept = () => close(current.kind === 'prompt' ? value : true);

  const [title, body] = splitMessage(current.message);

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) cancel(); }}
    >
      <form
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        onSubmit={(e) => { e.preventDefault(); accept(); }}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
      >
        <h2 id="dialog-title">{title}</h2>
        {body !== null && <p className="dialog-body">{body}</p>}

        {current.kind === 'prompt' && (
          <div className="field">
            {current.multiline === true ? (
              <textarea ref={fieldRef} rows={3} value={value} aria-labelledby="dialog-title"
                onChange={(e) => setValue(e.target.value)} />
            ) : (
              <input ref={fieldRef} value={value} aria-labelledby="dialog-title"
                onChange={(e) => setValue(e.target.value)} />
            )}
            {current.hint !== undefined && <span className="hint">{current.hint}</span>}
          </div>
        )}

        <div className="dialog-actions">
          <button type="button" className="btn secondary" onClick={cancel}>
            {current.cancelLabel ?? 'Cancel'}
          </button>
          <button ref={confirmRef} type="submit" className={current.danger === true ? 'btn danger' : 'btn'}>
            {current.confirmLabel ?? (current.kind === 'prompt' ? 'Continue' : 'Yes, continue')}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Title and explanation from one message: at a blank line if there is one;
 * otherwise, for a long single paragraph, after its first sentence - so the title
 * stays a short question and the detail reads as detail.
 */
function splitMessage(message: string): [string, string | null] {
  const blank = message.indexOf('\n\n');
  if (blank !== -1) return [message.slice(0, blank), message.slice(blank + 2)];

  if (message.length > 90) {
    const sentence = /^(.{8,140}?[.?!])\s+([\s\S]+)$/.exec(message);
    if (sentence?.[1] !== undefined && sentence[2] !== undefined) {
      return [sentence[1], sentence[2]];
    }
  }

  return [message, null];
}
